import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  IconButton,
  Snackbar,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  PlayArrow as LoadIcon,
  Save as SaveIcon,
  FileDownload as ExportIcon,
  RemoveRedEye as PreviewIcon,
  Search as SearchIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
} from '@mui/icons-material';
import {
  addElement,
  removeElement,
  toggleElement,
  updateSelector,
  updateMatchCount,
  reorderElements,
  setPickerActive,
  setTargetUrl,
  togglePreviewMode,
  clearAllElements,
  setError,
} from '../redux/slices/elementHiderSlice';
import axios from 'axios';
import { API_BASE_URL } from '../config';

export default function ElementHiderPage() {
  const dispatch = useDispatch();
  const {
    hiddenElements,
    pickerActive,
    targetUrl,
    previewMode,
    error,
  } = useSelector((state) => state.elementHider);

  const [urlInput, setUrlInput] = useState('');
  const [manualSelector, setManualSelector] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [editingSelector, setEditingSelector] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const selectedElement = hiddenElements.find((el) => el.id === selectedId);

  const showMessage = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleLoadUrl = () => {
    if (!urlInput.trim()) return;
    dispatch(setTargetUrl(urlInput.trim()));
    showMessage('URL loaded. Use Activate Picker to select elements via the extension.');
  };

  // Listen for element picker results from the extension via the frontend bridge
  useEffect(() => {
    const handler = (event) => {
      if (event.data?.source !== 'automatrix-extension') return;
      if (event.data.type === 'ELEMENT_PICKER_RESULT' && event.data.payload?.selections) {
        const selections = event.data.payload.selections;
        selections.forEach((sel) => {
          dispatch(addElement({
            selector: sel.selector,
            elementTag: sel.tagName || '',
            elementClasses: '',
            textSnippet: '',
          }));
        });
        dispatch(setPickerActive(false));
        showMessage(`${selections.length} element(s) selected from picker`);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [dispatch, showMessage]);

  const handleActivatePicker = () => {
    const newState = !pickerActive;
    dispatch(setPickerActive(newState));
    // Send message to extension via the frontend bridge (window.postMessage)
    window.postMessage(
      {
        source: 'automatrix-frontend',
        requestId: 'picker-' + Date.now(),
        payload: {
          action: newState ? 'ACTIVATE_ELEMENT_PICKER' : 'DEACTIVATE_ELEMENT_PICKER',
          tabId: -1, // let background figure out the active tab
        },
      },
      '*'
    );
  };

  const handleAddManualRule = () => {
    if (!manualSelector.trim()) return;
    dispatch(addElement({ selector: manualSelector.trim() }));
    setManualSelector('');
    showMessage('Rule added');
  };

  const handleSelectElement = (id) => {
    setSelectedId(id);
    const el = hiddenElements.find((e) => e.id === id);
    if (el) {
      setEditingSelector(el.selector);
    }
  };

  const handleApplyEdit = () => {
    if (selectedId && editingSelector.trim()) {
      dispatch(updateSelector({ id: selectedId, selector: editingSelector.trim() }));
      showMessage('Selector updated');
    }
  };

  const handleRevert = () => {
    if (selectedElement) {
      setEditingSelector(selectedElement.selector);
    }
  };

  const handleTestSelector = () => {
    if (!editingSelector.trim()) return;
    try {
      // Try to count matches in the iframe or current document
      const count = document.querySelectorAll(editingSelector.trim()).length;
      if (selectedId) {
        dispatch(updateMatchCount({ id: selectedId, matchCount: count }));
      }
      showMessage(`${count} element(s) match this selector`);
    } catch (e) {
      showMessage('Invalid CSS selector', 'error');
    }
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const newList = [...hiddenElements];
    const temp = newList[index - 1];
    newList[index - 1] = newList[index];
    newList[index] = temp;
    dispatch(reorderElements(newList));
  };

  const handleMoveDown = (index) => {
    if (index === hiddenElements.length - 1) return;
    const newList = [...hiddenElements];
    const temp = newList[index + 1];
    newList[index + 1] = newList[index];
    newList[index] = temp;
    dispatch(reorderElements(newList));
  };

  const handleExportCss = () => {
    const activeRules = hiddenElements.filter((el) => el.isHidden);
    if (activeRules.length === 0) {
      showMessage('No active hide rules to export', 'warning');
      return;
    }
    const css = activeRules
      .map((el) => `${el.selector} { display: none !important; }`)
      .join('\n');
    const blob = new Blob([css], { type: 'text/css' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'element-hide-rules.css';
    a.click();
    URL.revokeObjectURL(url);
    showMessage('CSS file exported');
  };

  const handleSaveAsContentFilter = async () => {
    const activeRules = hiddenElements.filter((el) => el.isHidden);
    if (activeRules.length === 0) {
      showMessage('No active hide rules to save', 'warning');
      return;
    }
    try {
      const payload = {
        name: `Element Hide Rules - ${targetUrl || 'Manual'}`,
        description: `Auto-generated element hide rules (${activeRules.length} selectors)`,
        rules: activeRules.map((el, idx) => ({
          priority: idx + 1,
          action: 'ELEMENT_HIDE',
          urlPattern: targetUrl || '*',
          cssSelector: el.selector,
        })),
      };
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/network-rule-sets`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showMessage('Saved as Content Filter Rule Set');
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || 'Failed to save';
      dispatch(setError(msg));
      showMessage(msg, 'error');
    }
  };

  const handleUnhideAll = () => {
    dispatch(clearAllElements());
    setSelectedId(null);
    showMessage('All hide rules removed');
  };

  return (
    <Box p={3}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {/* Header */}
      <Typography variant="h5" sx={{ mb: 3 }}>Element Hider</Typography>

      {/* Top Section: URL input and picker controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} alignItems="center" mb={2}>
          <TextField
            size="small"
            placeholder="Enter URL to inspect"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleLoadUrl()}
            sx={{ flexGrow: 1 }}
          />
          <Button
            variant="contained"
            startIcon={<LoadIcon />}
            onClick={handleLoadUrl}
          >
            Load
          </Button>
        </Box>

        {targetUrl && (
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              height: 200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'grey.50',
              mb: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary" align="center">
              Target: <strong>{targetUrl}</strong>
              <br />
              Use the Automatrix browser extension to interact with this page.
              <br />
              The extension element picker will overlay the target page for visual selection.
            </Typography>
          </Box>
        )}

        <Box display="flex" gap={2} alignItems="center">
          <Button
            variant={pickerActive ? 'contained' : 'outlined'}
            color={pickerActive ? 'secondary' : 'primary'}
            onClick={handleActivatePicker}
            disabled={!targetUrl}
          >
            {pickerActive ? 'Deactivate Picker' : 'Activate Picker'}
          </Button>
          <Chip
            label={pickerActive ? 'Picker Active' : 'Picker Inactive'}
            color={pickerActive ? 'success' : 'default'}
            variant="outlined"
            size="small"
          />
        </Box>
      </Paper>

      {/* Main Area: Split View */}
      <Box display="flex" gap={3} sx={{ mb: 3 }}>

        {/* Left Panel (60%): Hidden Elements List */}
        <Paper sx={{ flex: '0 0 60%', p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Hidden Elements</Typography>
            <Chip
              label={`${hiddenElements.filter((e) => e.isHidden).length} active rules`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>

          {/* Add Manual Rule */}
          <Box display="flex" gap={1} mb={2}>
            <TextField
              size="small"
              placeholder="Add CSS selector (e.g. .ad-banner, #popup)"
              value={manualSelector}
              onChange={(e) => setManualSelector(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddManualRule()}
              sx={{ flexGrow: 1 }}
            />
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddManualRule}
              disabled={!manualSelector.trim()}
            >
              Add Manual Rule
            </Button>
          </Box>

          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Selector</TableCell>
                  <TableCell>Element Preview</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {hiddenElements.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No hide rules yet. Use the element picker or add a manual rule.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  hiddenElements.map((el, index) => (
                    <TableRow
                      key={el.id}
                      hover
                      selected={selectedId === el.id}
                      onClick={() => handleSelectElement(el.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Tooltip title={el.selector}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {el.selector}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 150 }}>
                          {el.elementTag ? `<${el.elementTag}>` : '--'}
                          {el.elementClasses ? `.${el.elementClasses.split(' ').join('.')}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={el.isHidden ? 'Hidden' : 'Visible'}
                          color={el.isHidden ? 'error' : 'success'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" justifyContent="flex-end" gap={0.5}>
                          <Tooltip title="Move up">
                            <span>
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleMoveUp(index); }} disabled={index === 0}>
                                <UpIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Move down">
                            <span>
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleMoveDown(index); }} disabled={index === hiddenElements.length - 1}>
                                <DownIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={el.isHidden ? 'Show element' : 'Hide element'}>
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); dispatch(toggleElement(el.id)); }}>
                              {el.isHidden ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleSelectElement(el.id); }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); dispatch(removeElement(el.id)); if (selectedId === el.id) setSelectedId(null); }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Right Panel (40%): Rule Details / Editor */}
        <Paper sx={{ flex: '0 0 calc(40% - 24px)', p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Rule Details</Typography>

          {selectedElement ? (
            <Box>
              <TextField
                label="CSS Selector"
                size="small"
                fullWidth
                value={editingSelector}
                onChange={(e) => setEditingSelector(e.target.value)}
                sx={{ mb: 2, fontFamily: 'monospace' }}
                InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
              />

              <Button
                variant="outlined"
                size="small"
                startIcon={<SearchIcon />}
                onClick={handleTestSelector}
                sx={{ mb: 2 }}
              >
                Test Selector
              </Button>

              {selectedElement.matchCount > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {selectedElement.matchCount} element(s) match this selector
                </Alert>
              )}

              <Divider sx={{ mb: 2 }} />

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Element Info
              </Typography>
              <Box sx={{ mb: 2, pl: 1 }}>
                <Typography variant="body2">
                  <strong>Tag:</strong> {selectedElement.elementTag || 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Classes:</strong> {selectedElement.elementClasses || 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Text snippet:</strong> {selectedElement.textSnippet || 'N/A'}
                </Typography>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleApplyEdit}
                  disabled={editingSelector === selectedElement.selector}
                >
                  Apply
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleRevert}
                  disabled={editingSelector === selectedElement.selector}
                >
                  Revert
                </Button>
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Select a rule from the list to view and edit its details.
            </Typography>
          )}
        </Paper>
      </Box>

      {/* Bottom Section */}
      <Paper sx={{ p: 2 }}>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <FormControlLabel
            control={
              <Switch
                checked={previewMode}
                onChange={() => dispatch(togglePreviewMode())}
                size="small"
              />
            }
            label="Preview Mode"
          />
          <Tooltip title="Preview applies all active hide rules to see the combined effect">
            <Chip
              icon={<PreviewIcon />}
              label={previewMode ? 'Preview ON' : 'Preview OFF'}
              color={previewMode ? 'primary' : 'default'}
              size="small"
              variant="outlined"
            />
          </Tooltip>

          <Box sx={{ flexGrow: 1 }} />

          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={handleExportCss}
            size="small"
          >
            Export as CSS
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveAsContentFilter}
            size="small"
          >
            Save as Content Filter Rule
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleUnhideAll}
            size="small"
          >
            Unhide All
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
